import { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useCells, useFineScores, useFriseScores, useScores, useTimelines, useZone } from './api/queries';
import { formatLong } from './lib/dates';
import { resolutionForZoom } from './lib/resolution';
import type { Bbox, LocateOutcome, OutingOverlay } from './map/MapView';
import { useApp } from './store';
import { BottomSheet } from './ui/BottomSheet';
import { Frise, type FriseSeries } from './ui/Frise';
import { Legend } from './ui/Legend';
import { SpeciesFilter } from './ui/SpeciesFilter';
import { IconMenu, IconWalk } from './ui/icons';
import { Onboarding, onboardingDone } from './ui/Onboarding';
import { StatusBanner } from './ui/StatusBanner';
import { useOnline } from './lib/online';
import { useQueryClient } from '@tanstack/react-query';
import './styles/components.css';
import './app.css';

// MapLibre et h3-js pèsent lourd : la carte et la fiche arrivent après la première peinture.
const loadMap = () => import('./map/MapView');
const MapView = lazy(() => loadMap().then((m) => ({ default: m.MapView })));
const CellCard = lazy(() => import('./ui/CellCard').then((m) => ({ default: m.CellCard })));
// Mode sortie : chargé seulement quand on part en forêt.
const loadOuting = () => import('./ui/outing/OutingMode');
const OutingStart = lazy(() => loadOuting().then((m) => ({ default: m.OutingStart })));
const OutingBarLive = lazy(() => loadOuting().then((m) => ({ default: m.OutingBarLive })));
const OutingPanelLive = lazy(() => loadOuting().then((m) => ({ default: m.OutingPanelLive })));
const OutingMapFeed = lazy(() => loadOuting().then((m) => ({ default: m.OutingMapFeed })));
const Menu = lazy(() => import('./ui/menu/Menu').then((m) => ({ default: m.Menu })));
const RulesPanel = lazy(() => import('./ui/menu/Menu').then((m) => ({ default: m.RulesPanel })));
const MyOutings = lazy(() => import('./ui/menu/MyOutings').then((m) => ({ default: m.MyOutings })));
const MethodPanel = lazy(() => import('./ui/methode/MethodPanel').then((m) => ({ default: m.MethodPanel })));
/** Lance le téléchargement de la carte sans attendre le rendu React. */
export const preloadMap = () => void loadMap();

function useMedia(query: string): boolean {
  const media = typeof matchMedia === 'undefined' ? null : matchMedia(query);
  return useSyncExternalStore(
    (cb) => {
      media?.addEventListener('change', cb);
      return () => media?.removeEventListener('change', cb);
    },
    () => media?.matches ?? false,
    () => false,
  );
}

const LOCATE_MESSAGES: Record<LocateOutcome, string | null> = {
  'dans-la-zone': null,
  'hors-zone': "Vous êtes hors de la zone test : cette zone n'est pas encore couverte.",
  refusee: 'Localisation refusée. La carte marche sans ; vous pourrez l’activer dans les réglages du navigateur.',
  indisponible: 'Position indisponible pour le moment.',
};

export function App() {
  const app = useApp();
  const { shown, focus, date, selected, sheet, bbox, zoom, mode, outingId, outingTarget, panel, homeRequest } = app;
  const online = useOnline();
  const zone = useZone();
  const today = zone.data?.today ?? null;
  const scores = useScores(shown, date);
  const fineRes = resolutionForZoom(zoom);
  const fineScores = useFineScores(shown, date, fineRes, bbox);
  // Avant que la carte ne donne son emprise, la frise part de celle de la zone : elle n'attend pas MapLibre.
  const timelines = useTimelines(shown, bbox ?? zone.data?.bbox ?? null);
  const cells = useCells(selected, shown, date);
  const isWide = useMedia('(min-width: 960px)');
  // Téléphone en paysage : le panneau passe à gauche (voir bottom-sheet.css).
  const isLandscapePhone = useMedia('(orientation: landscape) and (max-height: 520px)');
  const [sheetPx, setSheetPx] = useState(240);
  // Hauteur de la barre du haut (filtre ou barre de sortie) : le panneau plein écran s'arrête dessous en sortie.
  const hautRef = useRef<HTMLDivElement>(null);
  const [hautPx, setHautPx] = useState(0);
  useEffect(() => {
    const el = hautRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setHautPx(Math.ceil(el.getBoundingClientRect().bottom)));
    observer.observe(el);
    return () => observer.disconnect();
  }, [isWide]); // la barre du haut n'existe que dans la mise en page mobile
  const [announce, setAnnounce] = useState<string | null>(null);
  const [outingOverlay, setOutingOverlay] = useState<OutingOverlay | null>(null);
  // Les jours voisins se préchargent une fois la première carte affichée, quand le navigateur est libre.
  const [warm, setWarm] = useState(false);
  const firstReady = Object.keys(scores).length > 0;
  useEffect(() => {
    if (!firstReady || warm) return;
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    const id = idle(() => setWarm(true), { timeout: 4000 });
    return () => (window.cancelIdleCallback ?? window.clearTimeout)(id);
  }, [firstReady, warm]);
  useFriseScores(shown, today, date, warm);

  // La carte ne fait pas partie de la coque prérendue : elle se monte une fois l'interface hydratée.
  const [hydrated, setHydrated] = useState(false);
  // Premier lancement : ce que fait la carte, les règles, la localisation (une seule fois).
  // Rendu dans la coque prérendue (caché par CSS sauf au premier lancement), retiré après l'hydratation s'il a déjà été vu.
  const [firstLaunch, setFirstLaunch] = useState(true);
  useEffect(() => {
    app.restore();
    setHydrated(true);
    setFirstLaunch(!onboardingDone());
    // Synchronisation des sorties, et reprise d'une sortie restée ouverte (onglet fermé, téléphone redémarré).
    void import('./outing/outing').then(({ currentOuting, startBackgroundSync }) => {
      startBackgroundSync();
      void currentOuting().then((id) => id && useApp.getState().enterOuting(id));
    });
  }, []);

  useEffect(() => {
    if (today && !date) app.setDate(today);
  }, [today, date]);

  useEffect(() => {
    if (!announce) return;
    const t = setTimeout(() => setAnnounce(null), 6000);
    return () => clearTimeout(t);
  }, [announce]);

  // Échap ferme la fiche, où que soit le focus.
  useEffect(() => {
    if (!selected) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') app.select(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const onLocate = useCallback((outcome: LocateOutcome) => setAnnounce(LOCATE_MESSAGES[outcome]), []);
  const onView = useCallback((b: Bbox, z: number) => useApp.getState().setView(b, z), []);
  const prepareOuting = (target: string | null) => {
    app.prepareOuting(target);
    app.setSheet('plein');
  };

  const padding = useMemo(
    () =>
      isWide
        ? { top: 16, right: 16, bottom: 16, left: 16 }
        : isLandscapePhone
          ? { top: 72, right: 0, bottom: 0, left: Math.min(420, Math.round(window.innerWidth * 0.55)) }
          : { top: mode === 'sortie' ? 120 : 72, right: 0, bottom: sheetPx, left: 0 },
    [isWide, isLandscapePhone, sheetPx, mode],
  );

  const firstScores = scores[shown[0]!];
  const shownDate = firstScores?.data_date ?? date;
  const scoreQueries = useQueryClient();
  // L'état de la requête du jour suffit : useScores fait déjà réafficher quand il change.
  const scoresFailed = date ? scoreQueries.getQueryState(['scores', shown[0], date])?.status === 'error' : false;
  // Hors de la zone couverte : l'emprise visible ne touche plus le rectangle de la zone test.
  const zb = zone.data?.bbox;
  const outOfZone = !!(zb && bbox && (bbox[2] < zb[0] || bbox[0] > zb[2] || bbox[3] < zb[1] || bbox[1] > zb[3]));
  const banner = (
    <StatusBanner
      online={online}
      staleDate={firstScores && date && firstScores.data_date !== date ? firstScores.data_date : null}
      failed={scoresFailed || zone.isError}
      hasData={!!firstScores}
      outOfZone={outOfZone}
      onRetry={() => void scoreQueries.refetchQueries()}
      onHome={app.goHome}
    />
  );
  const menuButton = (
    <button type="button" className="bouton-carte bouton-menu" onClick={() => app.openPanel('menu')} aria-label="Menu : mes sorties, méthode, règles, réglages">
      <IconMenu />
    </button>
  );
  const series: FriseSeries[] = shown.flatMap((s) => (timelines[s] ? [{ species: s, days: timelines[s]!.days }] : []));

  const frise = (
    <Frise series={series} today={today} selected={date} onSelect={app.setDate} mode="zone" label="Jour affiché sur la carte" />
  );

  const startButton = (
    <button type="button" className="bouton bouton-principal" onClick={() => prepareOuting(null)} data-apercu>
      <IconWalk /> Démarrer une sortie
    </button>
  );

  const card =
    selected && date && today ? (
      <Suspense fallback={<p className="fiche-surtitre" data-apercu>Chargement de la fiche…</p>}>
        <CellCard
          details={cells.data}
          loading={cells.loading}
          failed={cells.failed}
          errorCode={cells.errorCode}
          shown={shown}
          focus={focus}
          onFocus={app.setFocus}
          date={date}
          today={today}
          onDate={app.setDate}
          onClose={() => app.select(null)}
          onStartOuting={() => prepareOuting(selected)}
          closeStyle={isWide ? 'retour' : 'croix'}
        />
      </Suspense>
    ) : null;

  const reading = (
    <>
      <Legend shown={shown} />
      <p className="aide">
        Chaque hexagone couvre environ 70 hectares ; en zoomant, il se divise en mailles de 10 puis 1,5 hectare, là où il y a de la
        forêt. Plus la teinte est foncée, plus la pousse est probable ce jour-là. Touchez une maille pour savoir pourquoi.
      </p>
      <button type="button" className="bouton bouton-secondaire lien-methode" onClick={() => app.openPanel('methode')}>
        Comment la carte est calculée
      </button>
    </>
  );

  const backToMenu = () => app.openPanel('menu');
  const panelContent = panel ? (
    <Suspense fallback={<p data-apercu>Chargement…</p>}>
      {panel === 'menu' && <Menu onClose={() => app.openPanel(null)} onOpen={app.openPanel} />}
      {panel === 'methode' && <MethodPanel onClose={() => app.openPanel(null)} weatherAt={firstScores?.generated_at ?? null} />}
      {panel === 'regles' && <RulesPanel onBack={backToMenu} />}
      {panel === 'sorties' && (
        <MyOutings
          onBack={backToMenu}
          onStart={() => {
            app.openPanel(null);
            prepareOuting(null);
          }}
        />
      )}
    </Suspense>
  ) : null;

  const outingContent =
    mode === 'avant-sortie' ? (
      <Suspense fallback={<p data-apercu>Chargement…</p>}>
      <OutingStart
        target={outingTarget}
        onStarted={(id) => {
          app.enterOuting(id);
          app.setSheet('replie');
        }}
        onCancel={() => app.leaveOuting()}
      />
      </Suspense>
    ) : mode === 'sortie' && outingId ? (
      <Suspense fallback={<p data-apercu>Chargement…</p>}>
        <OutingPanelLive outingId={outingId} defaultSpecies={focus} onLeave={() => app.leaveOuting()} />
        <OutingMapFeed outingId={outingId} onOverlay={setOutingOverlay} />
      </Suspense>
    ) : null;

  const map = hydrated ? (
    <Suspense fallback={<div className="carte" />}>
      <MapView
        zone={zone.data}
        scores={scores}
        fine={fineRes ? { resolution: fineRes, scores: fineScores } : null}
        shown={shown}
        selected={mode === 'carte' ? selected : null}
        padding={padding}
        outing={mode === 'sortie' ? outingOverlay : null}
        onSelect={(h3) => {
          if (mode !== 'carte') return;
          // Toucher hors des mailles sans fiche ouverte : dire pourquoi rien ne s'ouvre.
          if (!h3 && !selected) setAnnounce('Pas de forêt notée ici : touchez une maille colorée.');
          app.select(h3);
        }}
        onView={onView}
        onLocate={onLocate}
        homeRequest={homeRequest}
      />
    </Suspense>
  ) : (
    <div className="carte" />
  );

  const status = (
    <p className={announce ? 'annonce annonce-visible' : 'annonce'} role="status">
      {announce}
    </p>
  );

  const outingBar =
    mode === 'sortie' && outingId ? (
      <Suspense fallback={null}>
        <OutingBarLive outingId={outingId} />
      </Suspense>
    ) : null;

  if (isWide) {
    return (
      <div className="app app-large">
        {firstLaunch && <Onboarding onDone={() => setFirstLaunch(false)} />}
        <aside className="panneau-lateral" tabIndex={-1} aria-label={outingContent ? 'Sortie' : selected ? 'Fiche de la maille' : 'Carte du jour'}>
          {outingBar}
          {outingContent ?? (
            <>
              <header className="titre">
                <div className="titre-ligne">
                  <h1 className="marque">Pousse</h1>
                  {menuButton}
                </div>
                <SpeciesFilter />
              </header>
              {banner}
              {panelContent ?? card ?? (
                <>
                  <p className="jour">{shownDate ? `Carte du ${formatLong(shownDate)}` : 'Chargement de la carte du jour…'}</p>
                  {reading}
                </>
              )}
            </>
          )}
        </aside>
        <main className="zone-carte">
          {map}
          {status}
        </main>
        <section className="barre-frise" aria-label="Frise de pousse : un mois passé et la semaine à venir">
          {frise}
          {mode === 'carte' && startButton}
        </section>
      </div>
    );
  }

  return (
    <div className={mode === 'sortie' ? 'app app-sortie' : 'app'}>
      <h1 className="visually-hidden">Pousse : carte de pousse du cèpe et de la girolle</h1>
      {firstLaunch && <Onboarding onDone={() => setFirstLaunch(false)} />}
      <main className="zone-carte">{map}</main>
      <div className="haut" ref={hautRef}>
        {outingBar ?? (
          mode === 'carte' && (
            <div className="haut-ligne">
              <SpeciesFilter floating />
              {menuButton}
            </div>
          )
        )}
        {mode !== 'sortie' && banner}
        {status}
      </div>
      <BottomSheet
        height={sheet}
        onHeight={app.setSheet}
        label={outingContent ? 'Sortie' : panel ? 'Menu' : selected ? 'Fiche de la maille' : 'Frise de pousse et carte du jour'}
        onVisibleHeight={setSheetPx}
        contentKey={`${mode}:${panel ?? selected ?? 'accueil'}`}
        topInset={mode === 'sortie' ? hautPx + 8 : 0}
      >
        {outingContent ??
          panelContent ??
          card ?? (
            <div className="accueil">
              {frise}
              {startButton}
              <div className="accueil-suite">{reading}</div>
            </div>
          )}
      </BottomSheet>
    </div>
  );
}
